// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Button, Typography } from '@mui/material'
import classNames from 'classnames'
import React, { Component, ReactNode } from 'react'
import { checklistItemTypes } from '../admin/checklist/constants'
import { i18n } from '../index'
import checklistCss from './Checklist.css'
import ChecklistTaskRow from './ChecklistTaskRow'
import css from './ChecklistTaskSection.css'
import ContinuingChecklistTaskRow from './ContinuingChecklistTaskRow'
import type { ChecklistTaskRecord, ContinuingChecklistItem } from './types'
type Props = {
  tasks: Array<ChecklistTaskRecord>
  date: string
  currentTime: Moment
  continuingChecklistItems: Array<ContinuingChecklistItem>
  stationId: string
}

const SHIFTS = ['morning', 'evening', 'night']

const taskRecordIsCheckedCompletely = (record?: ChecklistTaskRecord): boolean => {
  if (!record || !record.shiftStatus) {
    return false
  }
  const availableItemShiftStatuses = Object.values(record?.checklistItemShiftStatus ?? []).reduce(
    (acc, cur) => acc + (cur === 'Available' ? 1 : 0),
    0,
  )
  const checkStatuses = Object.values(record?.shiftStatus ?? []).reduce(
    (acc, cur) => acc + (cur === 'Checked' ? 1 : 0),
    0,
  )
  return availableItemShiftStatuses - checkStatuses === 0
}

const divideListByCompletelyChecked = (
  records?: ChecklistTaskRecord[],
): { completelyChecked: ChecklistTaskRecord[]; rest: ChecklistTaskRecord[] } => {
  return (
    records?.reduce(
      (acc, cur) => {
        if (taskRecordIsCheckedCompletely(cur)) {
          return { ...acc, completelyChecked: [...acc.completelyChecked, cur] }
        }
        return { ...acc, rest: [...acc.rest, cur] }
      },
      { completelyChecked: [], rest: [] },
    ) ?? { completelyChecked: [], rest: [] }
  )
}

const TaskList = ({ tasks, continuingChecklistItems, date, currentTime, sort, sortOrder, sortBy, stationId }) => {
  const getJoinedTaskTypes = (types: string[] | null) => {
    if (!types) {
      return ''
    } else {
      return types.map((type) => i18n.t(`checkList.type.${type}`)).join(', ')
    }
  }
  const sortedContinuingChecklistTasks = [
    ...continuingChecklistItems.filter((item) => item.interval === 'Daily'),
    ...continuingChecklistItems.filter((item) => item.interval === 'Weekly'),
    ...continuingChecklistItems.filter((item) => item.interval === 'Monthly'),
  ]

  return (
    <React.Fragment>
      <table className={checklistCss.table}>
        <thead>
          <tr>
            <SortingHeader sort={sort} sortOrder={sortOrder} sortBy={sortBy} field='task' width='50%'>
              {i18n.t('checkList.task')}
            </SortingHeader>
            <SortingHeader sortOrder={sortOrder} sortBy={sortBy} field='type' width='25%' sort={sort}>
              {i18n.t('checkList.typeHeading')}
            </SortingHeader>
            <SortingHeader sortOrder={sortOrder} sortBy={sortBy} field='morning' sort={sort}>
              {i18n.t('shifts.morning')}
            </SortingHeader>
            <SortingHeader sortOrder={sortOrder} sortBy={sortBy} field='evening' sort={sort}>
              {i18n.t('shifts.evening')}
            </SortingHeader>
            <SortingHeader sortOrder={sortOrder} sortBy={sortBy} field='night' sort={sort}>
              {i18n.t('shifts.night')}
            </SortingHeader>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task, index) => (
            <ChecklistTaskRow
              key={index}
              checklistItemId={task.checklistItemId}
              description={task.description}
              type={getJoinedTaskTypes(task.type)}
              shiftStatus={task.shiftStatus || task.checklistItemShiftStatus}
              date={date}
              currentTime={currentTime}
            />
          ))}
        </tbody>
      </table>

      {sortedContinuingChecklistTasks.length > 0 && (
        <table className={classNames(checklistCss.table, checklistCss.continuingTable)}>
          <thead>
            <tr>
              <th
                style={{
                  width: '50%',
                }}
                className={checklistCss.header}
              >
                {i18n.t('checkList.task')}
              </th>
              <th
                style={{
                  width: '25%',
                }}
                className={checklistCss.header}
              >
                {i18n.t('checkList.typeHeading')}
              </th>
              <th className={checklistCss.checkHeader}>{i18n.t('checkList.continuingChecklist.daily')}</th>
              <th className={checklistCss.checkHeader}>{i18n.t('checkList.continuingChecklist.weekly')}</th>
              <th className={checklistCss.checkHeader}>{i18n.t('checkList.continuingChecklist.monthly')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedContinuingChecklistTasks.map((item, i) => (
              <ContinuingChecklistTaskRow key={i} item={item} stationId={stationId} date={date} />
            ))}
          </tbody>
        </table>
      )}
    </React.Fragment>
  )
}

export default class ChecklistTaskSection extends Component<Props> {
  state = {
    sortBy: undefined,
    sortOrder: undefined,
    filterTypes: {},
  }
  setSort = (sortBy: string) => {
    const order = this.state.sortOrder
    const nextSortOrder = this.state.sortOrder === 'asc' ? 'desc' : order === 'desc' ? undefined : 'asc'
    const nextSortBy = nextSortOrder && sortBy
    this.setState({
      sortBy: nextSortBy,
      sortOrder: nextSortOrder,
    })
  }
  setFilter = (type: string | null) => () => {
    const exists = this.state.filterTypes[type]
    const newState = { ...this.state.filterTypes }

    if (exists) {
      delete newState[type]
    } else {
      newState[type] = true
    }

    this.setState({
      filterTypes: newState,
    })
  }
  sortedTasks = (tasks: ChecklistTaskRecord[]) => {
    const { sortBy, sortOrder } = this.state
    if (sortBy === 'task') {
      return [...tasks].sort((a, b) => {
        if (a.description > b.description) return sortOrder === 'asc' ? 1 : -1
        if (a.description < b.description) return sortOrder === 'asc' ? -1 : 1
        return 0
      })
    }
    // Sort by shifts
    if (SHIFTS.includes(sortBy)) {
      return [...tasks].sort((a, b) => {
        const aIsShift = a.checklistItemShiftStatus[sortBy] === 'Available'
        const bIsShift = b.checklistItemShiftStatus[sortBy] === 'Available'
        if (!aIsShift && bIsShift) return sortOrder === 'asc' ? 1 : -1
        if (aIsShift && !bIsShift) return sortOrder === 'asc' ? -1 : 1
        return 0
      })
    }
    // If no sort is defined, sort by name
    return [...tasks].sort((a, b) => {
      const A = i18n.t(`checkList.${sortBy}.${a[sortBy]}`)
      const B = i18n.t(`checkList.${sortBy}.${b[sortBy]}`)

      if (A > B) {
        return sortOrder === 'asc' ? 1 : -1
      }

      if (A < B) {
        return sortOrder === 'asc' ? -1 : 1
      }

      return 0
    })
  }
  filterTasks = () => {
    const { filterTypes } = this.state
    const filters = Object.keys(filterTypes)
    return !filters.length
      ? this.props.tasks
      : this.props.tasks.filter(({ type }) => {
          return type && type.some((t) => filters.includes(t))
        })
  }

  render() {
    const { date, currentTime, continuingChecklistItems, stationId } = this.props
    const { sortOrder, sortBy, filterTypes } = this.state
    const filteredTasks = this.filterTasks()
    const { completelyChecked, rest } = divideListByCompletelyChecked(filteredTasks)
    const tasksToUse = sortBy ? this.sortedTasks(filteredTasks) : [...rest, ...completelyChecked]
    return (
      <React.Fragment>
        <div
          style={{
            width: '100%',
          }}
        >
          <div>
            <span id={'#johdettavat-tekemiset'} />
            <Typography variant='h5' className={checklistCss.sectionHeader}>
              {i18n.t('checkList.header')}
            </Typography>

            <div className={css.filterContainer}>
              <p className={css.filterHeading}>{i18n.t('checkList.filterByType')}:</p>

              {checklistItemTypes.map((type) => {
                return (
                  <Button
                    className={classNames(css.button, {
                      [css.selected]: filterTypes[type],
                    })}
                    key={type}
                    color={filterTypes[type] ? 'primary' : 'secondary'}
                    onClick={this.setFilter(type)}
                  >
                    {i18n.t(`checkList.type.${type}`)}
                  </Button>
                )
              })}
              <Button
                className={classNames(css.button, {
                  [css.selected]: filterTypes[null],
                })}
                color={filterTypes[null] ? 'primary' : 'secondary'}
                onClick={this.setFilter(null)}
              >
                {i18n.t(`checkList.type.NotDefined`)}
              </Button>
            </div>
          </div>
          <div>
            <TaskList
              sort={this.setSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
              tasks={tasksToUse}
              date={date}
              currentTime={currentTime}
              continuingChecklistItems={continuingChecklistItems}
              stationId={stationId}
            />
          </div>
        </div>
      </React.Fragment>
    )
  }
}

type SortingHeaderProps = {
  children: ReactNode
  sortOrder: string
  sortBy: string
  field: string
  width?: string
  sort: () => void
}

/**
 * Extraxt the structure for Sorting headings
 */
const SortingHeader = ({ children, sort, sortOrder, sortBy, field, width = 'auto' }: SortingHeaderProps) => {
  return (
    <th
      style={{
        width,
      }}
      className={checklistCss.header}
      onClick={() => sort(field)}
    >
      <div className={css.taskTypeHeaderContainer}>
        {children}
        <div className={css.headerCaretContainer}>
          {sortOrder && sortBy === field ? (
            <span className={`${css.caret} ${css[sortOrder]}`}></span>
          ) : (
            <React.Fragment>
              <span
                className={`${css.caret} ${css.desc}`}
                style={{
                  marginBottom: '3px',
                }}
              ></span>
              <span className={`${css.caret} ${css.asc}`}></span>
            </React.Fragment>
          )}
        </div>
      </div>
    </th>
  )
}
